// src/pages/Organizations/CreateOrganizationPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, TextField, Button, Box, Grid,
  FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Divider, GlobalStyles
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ArrowBack, Save, Business, ContactMail, LocationOn, Info } from '@mui/icons-material';
import api from '../../services/api';

// Rengigs Official Logo Palette
const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.06)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%)',
};

const scrollbarStyles = {
  '*::-webkit-scrollbar': { width: '10px', height: '10px' },
  '*::-webkit-scrollbar-track': { background: rengigsPalette.offWhite, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: rengigsPalette.forestGreen,
    backgroundImage: rengigsPalette.gradient,
    borderRadius: '10px',
    border: `2px solid ${rengigsPalette.offWhite}`,
    transition: 'all 0.3s ease',
  },
  '*::-webkit-scrollbar-thumb:hover': {
    backgroundColor: rengigsPalette.deepForest,
    backgroundImage: rengigsPalette.gradientReverse,
  },
  '*': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}`,
  },
};

const validationSchema = yup.object({
  name: yup.string().required('Organization name is required').max(255),
  legal_name: yup.string(),
  registration_number: yup.string(),
  organization_type: yup.string().required('Organization type is required'),
  size: yup.string().required('Company size is required'),
  industry: yup.string().required('Industry is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  website: yup.string().url('Enter a valid URL (include http/https)').nullable(),
  address_line1: yup.string().required('Address is required'),
  address_line2: yup.string(),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  country: yup.string().required('Country is required'),
  postal_code: yup.string().required('Postal code is required'),
  description: yup.string().max(2000),
});

const organizationTypes = [
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'sme', label: 'Small/Medium Enterprise' },
  { value: 'startup', label: 'Startup' },
  { value: 'nonprofit', label: 'Non-Profit' },
  { value: 'government', label: 'Government' },
  { value: 'educational', label: 'Educational Institution' },
];

const companySizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

// ✅ CRITICAL FIX: FormSection is defined OUTSIDE the main component.
// When it was inside, every keystroke caused React to treat it as a new
// component type, unmounting and remounting it — killing focus on every key press.
const FormSection = ({ icon: Icon, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
      <Box sx={{
        bgcolor: rengigsPalette.forestGreen,
        width: 32, height: 32, borderRadius: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 20, color: rengigsPalette.white }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: rengigsPalette.charcoal }}>
        {title}
      </Typography>
    </Box>
    <Grid container spacing={2.5}>{children}</Grid>
  </Box>
);

const CreateOrganizationPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(true);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoadingIndustries(true);
      const data = await api.get('/organizations/industries/');
      setIndustries(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      console.error('Error fetching industries:', err);
    } finally {
      setLoadingIndustries(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '', legal_name: '', registration_number: '', organization_type: '',
      size: '', industry: '', email: '', phone: '', website: '',
      address_line1: '', address_line2: '', city: '', state: '',
      country: '', postal_code: '', description: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        setError(null);
        // Clean up empty optional fields so backend doesn't get empty strings
        const payload = {
          ...values,
          website: values.website?.trim() || null,
          legal_name: values.legal_name?.trim() || '',
          registration_number: values.registration_number?.trim() || '',
          address_line2: values.address_line2?.trim() || '',
          description: values.description?.trim() || '',
        };
        await api.post('/organizations/', payload);
        navigate('/organizations');
      } catch (err) {
        if (err.data && typeof err.data === 'object') {
          const msgs = Object.entries(err.data)
            .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
            .join('\n');
          setError(msgs || 'Failed to create organization.');
        } else {
          setError(err.message || 'Failed to create organization.');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (loadingIndustries) {
    return (
      <>
        <GlobalStyles styles={scrollbarStyles} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organizations')}
          sx={{
            mb: 4, color: rengigsPalette.charcoal, textTransform: 'none', fontWeight: 600,
            '&:hover': { color: rengigsPalette.forestGreen, bgcolor: 'transparent' },
          }}
        >
          Back to Organizations
        </Button>

        <Paper elevation={0} sx={{
          p: { xs: 3, md: 6 }, borderRadius: '24px',
          border: `1px solid ${rengigsPalette.border}`, bgcolor: rengigsPalette.white,
        }}>
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" sx={{
              fontWeight: 800, color: rengigsPalette.charcoal, mb: 1,
              background: rengigsPalette.gradient,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text', display: 'inline-block',
            }}>
              Register Organization
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Set up your professional profile within the Rengigs ecosystem.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: '12px' }} onClose={() => setError(null)}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>

            {/* ── Basic Information ── */}
            <FormSection icon={Business} title="Basic Information">
              <Grid item xs={12}>
                <TextField
                  fullWidth name="name" label="Organization Name *"
                  value={formik.values.name}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="legal_name" label="Legal Name"
                  value={formik.values.legal_name}
                  onChange={formik.handleChange} disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="registration_number" label="Registration Number"
                  value={formik.values.registration_number}
                  onChange={formik.handleChange} disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  error={formik.touched.organization_type && Boolean(formik.errors.organization_type)}
                >
                  <InputLabel>Organization Type *</InputLabel>
                  <Select
                    name="organization_type" label="Organization Type *"
                    value={formik.values.organization_type}
                    onChange={formik.handleChange} disabled={submitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    {organizationTypes.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  error={formik.touched.size && Boolean(formik.errors.size)}
                >
                  <InputLabel>Company Size *</InputLabel>
                  <Select
                    name="size" label="Company Size *"
                    value={formik.values.size}
                    onChange={formik.handleChange} disabled={submitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    {companySizes.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl
                  fullWidth
                  error={formik.touched.industry && Boolean(formik.errors.industry)}
                >
                  <InputLabel>Industry *</InputLabel>
                  <Select
                    name="industry" label="Industry *"
                    value={formik.values.industry}
                    onChange={formik.handleChange} disabled={submitting}
                    sx={{ borderRadius: '12px' }}
                  >
                    {industries.map((i) => (
                      <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4, borderColor: rengigsPalette.border }} />

            {/* ── Contact & Digital ── */}
            <FormSection icon={ContactMail} title="Contact & Digital">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="email" label="Business Email *"
                  value={formik.values.email}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="phone" label="Phone Number *"
                  value={formik.values.phone}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth name="website" label="Website URL (https://...)"
                  value={formik.values.website}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.website && Boolean(formik.errors.website)}
                  helperText={formik.touched.website && formik.errors.website}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4, borderColor: rengigsPalette.border }} />

            {/* ── Location ── */}
            <FormSection icon={LocationOn} title="Location Details">
              <Grid item xs={12}>
                <TextField
                  fullWidth name="address_line1" label="Street Address *"
                  value={formik.values.address_line1}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.address_line1 && Boolean(formik.errors.address_line1)}
                  helperText={formik.touched.address_line1 && formik.errors.address_line1}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth name="address_line2" label="Address Line 2 (optional)"
                  value={formik.values.address_line2}
                  onChange={formik.handleChange} disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="city" label="City *"
                  value={formik.values.city}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="state" label="State / Province *"
                  value={formik.values.state}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="country" label="Country *"
                  value={formik.values.country}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.country && Boolean(formik.errors.country)}
                  helperText={formik.touched.country && formik.errors.country}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth name="postal_code" label="Postal Code *"
                  value={formik.values.postal_code}
                  onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={formik.touched.postal_code && Boolean(formik.errors.postal_code)}
                  helperText={formik.touched.postal_code && formik.errors.postal_code}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
            </FormSection>

            <Divider sx={{ my: 4, borderColor: rengigsPalette.border }} />

            {/* ── Additional Context ── */}
            <FormSection icon={Info} title="Additional Context">
              <Grid item xs={12}>
                <TextField
                  fullWidth multiline rows={4}
                  name="description" label="Organization Description"
                  placeholder="Briefly describe your core operations..."
                  value={formik.values.description}
                  onChange={formik.handleChange} disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
            </FormSection>

            {/* ── Actions ── */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 6 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/organizations')}
                disabled={submitting}
                sx={{
                  color: rengigsPalette.forestGreen, borderColor: rengigsPalette.forestGreen,
                  px: 4, borderRadius: '12px', textTransform: 'none', fontWeight: 600,
                  '&:hover': { borderColor: rengigsPalette.deepForest, bgcolor: rengigsPalette.softGreen },
                }}
              >
                Discard
              </Button>
              <Button
                type="submit" variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                sx={{
                  background: rengigsPalette.gradient, backgroundSize: '200% auto',
                  color: 'white', px: 4, py: 1.5, borderRadius: '12px',
                  fontWeight: 700, textTransform: 'none', fontSize: '1rem',
                  boxShadow: `0 10px 20px -5px ${rengigsPalette.forestGreen}40`,
                  transition: '0.3s',
                  '&:hover': {
                    background: rengigsPalette.forestGreen,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 24px -5px ${rengigsPalette.forestGreen}60`,
                  },
                  '&:disabled': { background: rengigsPalette.lightGreen, color: 'white' },
                }}
              >
                {submitting ? 'Processing...' : 'Create Organization'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default CreateOrganizationPage;